import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Target, Cpu, MemoryStick, Zap, PlayCircle, FileText, Activity, Clock, CheckCircle, AlertTriangle, Search, Bug, Key, Terminal, Globe, Server, Database, Wifi, Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import StatCard from '../../components/ui/StatCard';
import LLMSchema from '../../components/Dashboard/LLMSchema';
import { ScansMiniChart, CompletedMiniChart, VulnMiniChart } from '../../components/Dashboard/MiniCharts';
import WelcomeGuide from '../../components/WelcomeGuide';
import { health, dashboard as dashboardApi, system as systemApi, auth } from '../../api/client';
import { aiAssistantAPI } from '../../services/api/client';
import { usePageTitle } from '../../hooks/usePageTitle';
import PlanRestrictedButton from '../../components/PlanRestrictedButton';
import { usePlanRestrictions } from '../../hooks/usePlanRestrictions';
import { useI18n } from '../../i18n';

// Cache utilities
const CACHE_KEYS = {
  DASHBOARD_STATS: 'dashboard_stats_cache',
  DASHBOARD_SCANS: 'dashboard_scans_cache',
  DASHBOARD_HEALTH: 'dashboard_health_cache',
  DASHBOARD_HISTORY: 'dashboard_history_cache',
};

const CACHE_TTL = {
  STATS: 2 * 60 * 1000,      // 2 minutes (backend caches for 60s)
  SCANS: 2 * 60 * 1000,      // 2 minutes
  HEALTH: 2 * 60 * 1000,     // 2 minutes
  HISTORY: 5 * 60 * 1000     // 5 minutes
};

const getCache = (key) => {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    const { data, timestamp, ttl } = JSON.parse(cached);
    if (Date.now() - timestamp > ttl) {
      localStorage.removeItem(key);
      return null;
    }
    return data;
  } catch (e) {
    return null;
  }
};

const setCache = (key, data, ttl) => {
  try {
    localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now(), ttl }));
  } catch (e) {
    // Storage full or unavailable
  }
};

const Dashboard = () => {
  const { t } = useI18n();
  const translateScanType = (scanType) => {
    if (!scanType) return '-';
    const key = `scans.types.${scanType}`;
    const translated = t(key);
    if (translated !== key) return translated;
    const osintKey = `osint.scanTypes.${scanType}`;
    const osintTranslated = t(osintKey);
    if (osintTranslated !== osintKey) return osintTranslated;
    return scanType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };
  usePageTitle(t('dashboard.title', 'Dashboard - Security Monitoring'));
  const navigate = useNavigate();
  const { hasFeature, getQuotaStatus, isFreePlan, currentPlan } = usePlanRestrictions();

  // Initialize from cache for instant display
  const cachedStats = getCache(CACHE_KEYS.DASHBOARD_STATS);
  const cachedScans = getCache(CACHE_KEYS.DASHBOARD_SCANS);
  const cachedHealth = getCache(CACHE_KEYS.DASHBOARD_HEALTH);
  const cachedHistory = getCache(CACHE_KEYS.DASHBOARD_HISTORY);

  const [stats, setStats] = useState(cachedStats || {
    totalScans: 0,
    activeScans: 0,
    completedScans: 0,
    totalVulnerabilities: 0,
    criticalVulnerabilities: 0,
    highVulnerabilities: 0,
    mediumVulnerabilities: 0,
    lowVulnerabilities: 0,
  });
  const [activeScans, setActiveScans] = useState(cachedScans?.activeScans || []);
  const [recentActivity, setRecentActivity] = useState(cachedScans?.recentActivity || []);
  // If we have cached data, don't show loading spinner
  const [loading, setLoading] = useState(!cachedStats);
  const [systemHealth, setSystemHealth] = useState(cachedHealth);
  const [systemResources, setSystemResources] = useState({
    cpu: { percent: 0 },
    memory: { percent: 0 },
    gpu: { percent: 0, available: false, name: '', temperature: 0, memory_used_gb: 0, memory_total_gb: 0 }
  });
  const [historicalData, setHistoricalData] = useState(cachedHistory || {
    scans: [],
    completed: [],
    vulnerabilities: [],
  });

  // Dynamic state for LLM Schema
  const [scanStatus, setScanStatus] = useState(cachedStats?.activeScans > 0 ? 'scanning' : 'idle');

  // LLM status for AI/LLM card
  const [llmInfo, setLlmInfo] = useState(null);

  // Welcome Guide state
  const [showWelcomeGuide, setShowWelcomeGuide] = useState(false);

  // Track if initial data has been fetched
  const isInitialFetch = useRef(true);

  // Guard against state updates after unmount (prevents ERR_NETWORK_CHANGED side-effects)
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Check if we should show the welcome guide
  useEffect(() => {
    const shouldShow = localStorage.getItem('showWelcomeGuide') === 'true';
    const alreadyCompleted = localStorage.getItem('welcomeGuideCompleted') === 'true';
    if (shouldShow && !alreadyCompleted) {
      setShowWelcomeGuide(true);
    }
  }, []);

  // Dynamic attack tools based on active scans
  const attackTools = useMemo(() => {
    const hasActiveScan = stats.activeScans > 0;
    return [
      { icon: Search, title: t('dashboard.attackTools.reconnaissance'), sub: "Nmap, Shodan, OSINT", active: hasActiveScan },
      { icon: Bug, title: t('dashboard.attackTools.exploitation'), sub: "Metasploit, SQLMap", active: hasActiveScan && stats.totalVulnerabilities > 0 },
      { icon: Key, title: t('dashboard.attackTools.bruteForce'), sub: "Hydra, John the Ripper", active: false },
      { icon: Terminal, title: t('dashboard.attackTools.postExploitation'), sub: t('dashboard.attackTools.privilegeEscalation'), active: false }
    ];
  }, [stats.activeScans, stats.totalVulnerabilities, t]);

  // Dynamic targets based on recent scans
  const dynamicTargets = useMemo(() => {
    // Extract target types from recent scans
    const scanTargets = activeScans.slice(0, 4);

    if (scanTargets.length === 0) {
      // Default targets if no scans
      return [
        { icon: Globe, title: t('dashboard.targets.webApps'), sub: "Ports 80, 443, 8080", status: "idle" },
        { icon: Server, title: t('dashboard.targets.servers'), sub: "SSH, RDP, SMB", status: "idle" },
        { icon: Database, title: t('dashboard.targets.databases'), sub: "MySQL, PostgreSQL", status: "idle" },
        { icon: Wifi, title: t('dashboard.targets.network'), sub: t('dashboard.targets.networkDevices'), status: "idle" }
      ];
    }

    // Build targets from active scans
    return scanTargets.map((scan, idx) => {
      let icon = Globe;
      let title = scan.target || t('dashboard.targets.target');
      let sub = scan.scan_type || t('common.type');
      let status = 'idle';

      // Determine icon based on scan type
      if (scan.scan_type?.includes('web') || scan.scan_type?.includes('api')) {
        icon = Globe;
      } else if (scan.scan_type?.includes('network')) {
        icon = Wifi;
      } else if (scan.scan_type?.includes('db') || scan.scan_type?.includes('sql')) {
        icon = Database;
      } else {
        icon = Server;
      }

      // Determine status
      if (scan.status === 'running' || scan.status === 'pending' || scan.status === 'waiting') {
        status = 'scanning';
      } else if (scan.total_vulnerabilities > 0) {
        status = 'vulnerable';
      } else if (scan.status === 'completed') {
        status = 'secure';
      }

      // Tronquer le titre si trop long
      if (title.length > 20) {
        title = title.substring(0, 18) + '...';
      }

      return { icon, title, sub, status };
    });
  }, [activeScans, t]);

  useEffect(() => {
    let isMounted = true;
    const safeFetch = async () => { if (isMounted) await fetchDashboardData(); };
    safeFetch();
    // Refresh every 60 seconds (backend caches for 60s in Redis)
    const interval = setInterval(safeFetch, 60000);
    return () => { isMounted = false; clearInterval(interval); };
  }, []);

  // Real-time system resources monitoring (CPU, RAM, GPU)
  useEffect(() => {
    let isMounted = true;
    const fetchSystemResources = async () => {
      // Skip API call if token is invalid - prevents 401 errors during auth transitions
      if (!auth.isTokenValidClientSide()) return;

      try {
        const resourcesData = await systemApi.getResources();
        if (isMounted) setSystemResources(resourcesData);
      } catch (e) {
        // Silently handle expected errors (401/503) - user may be redirected to login
        // Only log unexpected errors in development
        if (e.response?.status !== 401 && e.response?.status !== 503) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Failed to fetch system resources:', e);
          }
        }
      }
    };

    // Initial fetch
    fetchSystemResources();

    // Update every 15 seconds (reduced from 5s to decrease load)
    const resourceInterval = setInterval(fetchSystemResources, 15000);

    return () => { isMounted = false; clearInterval(resourceInterval); };
  }, []);

  // Generate chart data based on historical or mock data
  const chartData = useMemo(() => {
    const today = new Date();
    const days = 7;

    // Generate last 7 days labels
    const generateDayLabels = () => {
      const labels = [];
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        labels.push(date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }));
      }
      return labels;
    };

    const dayLabels = generateDayLabels();

    // Use historical data if available, otherwise generate realistic mock data
    if (historicalData.scans.length > 0) {
      return {
        scans: historicalData.scans,
        completed: historicalData.completed,
        vulnerabilities: historicalData.vulnerabilities,
      };
    }

    // Generate mock data based on current stats
    const baseScans = Math.max(stats.totalScans / days, 3);
    const baseCompleted = Math.max(stats.completedScans / days, 2);
    const baseVulns = Math.max(stats.totalVulnerabilities / days, 5);

    const scansData = dayLabels.map((date, i) => ({
      date,
      scans: Math.round(baseScans + Math.sin(i * 0.8) * 3 + Math.random() * 2),
    }));

    const completedData = dayLabels.map((date, i) => {
      const total = scansData[i].scans;
      const completed = Math.round(total * (0.8 + Math.random() * 0.2));
      const pending = total - completed;
      return {
        date,
        completed,
        pending,
        total,
        rate: total > 0 ? Math.round((completed / total) * 100) : 100,
      };
    });

    const vulnData = dayLabels.map((date, i) => {
      const critical = Math.round(Math.max(0, stats.criticalVulnerabilities / days + Math.sin(i) * 1.5));
      const high = Math.round(Math.max(0, (stats.highVulnerabilities || baseVulns * 0.3) / days + Math.cos(i) * 2));
      const medium = Math.round(Math.max(0, (stats.mediumVulnerabilities || baseVulns * 0.4) / days + Math.sin(i + 1) * 3));
      const low = Math.round(Math.max(0, (stats.lowVulnerabilities || baseVulns * 0.3) / days + Math.random() * 2));
      return {
        date,
        critical,
        high,
        medium,
        low,
        total: critical + high + medium + low,
      };
    });

    return {
      scans: scansData,
      completed: completedData,
      vulnerabilities: vulnData,
    };
  }, [stats, historicalData]);

  const fetchDashboardData = async () => {
    // Guard: skip if component unmounted or token invalid
    if (!isMountedRef.current) return;
    if (!auth.isTokenValidClientSide()) {
      if (isMountedRef.current) setLoading(false);
      return;
    }

    try {
      // Health check (lightweight, no auth required)
      const healthData = await health.check();
      if (!isMountedRef.current) return;
      setSystemHealth(healthData);
      setCache(CACHE_KEYS.DASHBOARD_HEALTH, healthData, CACHE_TTL.HEALTH);

      // Dashboard stats - now returns ALL data in one call with Redis caching
      try {
        const dashStats = await dashboardApi.getStats('7d');
        if (!isMountedRef.current) return;

        if (dashStats.stats) {
          // Update stats directly from optimized endpoint
          setStats({
            totalScans: dashStats.stats.totalScans || 0,
            activeScans: dashStats.stats.activeScans || 0,
            completedScans: dashStats.stats.completedScans || 0,
            totalVulnerabilities: dashStats.stats.totalVulnerabilities || 0,
            criticalVulnerabilities: dashStats.stats.criticalVulnerabilities || 0,
            highVulnerabilities: dashStats.stats.highVulnerabilities || 0,
            mediumVulnerabilities: dashStats.stats.mediumVulnerabilities || 0,
            lowVulnerabilities: dashStats.stats.lowVulnerabilities || 0,
          });
          setCache(CACHE_KEYS.DASHBOARD_STATS, dashStats.stats, CACHE_TTL.STATS);
        }

        // Active scans from optimized endpoint
        if (dashStats.activeScans) {
          setActiveScans(dashStats.activeScans);
        }

        // Recent activity from optimized endpoint
        if (dashStats.recentActivity) {
          const activity = dashStats.recentActivity.map(scan => ({
            id: scan.id,
            type: scan.status === 'completed' ? 'scan_completed' : 'scan_started',
            title: `${t('scans.title')} ${t(`scans.status.${scan.status}`, scan.status)}: ${scan.target}`,
            description: `${t('common.type')}: ${translateScanType(scan.scan_type)}`,
            timestamp: scan.created_at,
          }));
          setRecentActivity(activity);
          setCache(CACHE_KEYS.DASHBOARD_SCANS, {
            activeScans: dashStats.activeScans || [],
            recentActivity: activity
          }, CACHE_TTL.SCANS);
        }

        // Historical data for charts
        if (dashStats.history) {
          setHistoricalData(dashStats.history);
          setCache(CACHE_KEYS.DASHBOARD_HISTORY, dashStats.history, CACHE_TTL.HISTORY);
        }

        // Update scan status for LLM Schema
        const activeCount = dashStats.stats?.activeScans || 0;
        const totalVulns = dashStats.stats?.totalVulnerabilities || 0;
        const completedCount = dashStats.stats?.completedScans || 0;

        if (activeCount > 0) {
          setScanStatus('scanning');
        } else if (totalVulns > 0) {
          setScanStatus('found_vulns');
        } else if (completedCount > 0) {
          setScanStatus('completed');
        } else {
          setScanStatus('idle');
        }
      } catch (e) {
        // Silently handle expected errors (401/503) - user may be redirected to login
        if (e.response?.status !== 401 && e.response?.status !== 503) {
          console.error('Dashboard stats error:', e);
        }
      }

      // Fetch LLM/AI status
      if (isMountedRef.current) {
        try {
          const aiStatus = await aiAssistantAPI.getStatus();
          if (isMountedRef.current) setLlmInfo(aiStatus?.gpu_pool || null);
        } catch (e) {
          // Non-critical - keep card with fallback values
        }
      }

    } catch (error) {
      // Silently handle expected errors (401/503) - user may be redirected to login
      if (error.response?.status !== 401 && error.response?.status !== 503) {
        console.error('Failed to fetch dashboard data:', error);
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-brand-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-black p-6 overflow-x-hidden">
      {/* Welcome Guide for new users */}
      {showWelcomeGuide && (
        <WelcomeGuide
          onClose={() => setShowWelcomeGuide(false)}
          onComplete={() => setShowWelcomeGuide(false)}
        />
      )}

      {/* Main container with 90% width for consistency */}
      <div className="w-[95%] sm:w-[92%] lg:w-[88%] laptop:w-[85%] 2xl:w-[80%] max-w-[1600px] laptop:max-w-[1700px] 2xl:max-w-[1800px] mx-auto space-y-6 overflow-hidden">
        {/* Header */}
        <div className="mb-8" data-tour="dashboard">
          <h1 className="text-4xl font-bold text-gradient-green mb-2">{t('dashboard.title')}</h1>
          <p className="text-gray-400">{t('dashboard.monitoring')}</p>
          {systemHealth && (
            <div className="mt-3 flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${systemHealth.status === 'healthy' ? 'bg-brand-green animate-pulse-green' : 'bg-red-500'}`}></div>
              <span className="text-sm text-gray-500">
                {t('dashboard.systemHealth')}: <span className={systemHealth.status === 'healthy' ? 'text-brand-green' : 'text-red-400'}>{systemHealth.status}</span>
              </span>
            </div>
          )}
        </div>

        {/* KPI Cards with Mini Charts */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-tour="dashboard-stats">
          {/* Total Scans */}
          <div onClick={() => navigate('/scans')} className="cursor-pointer">
            <StatCard
              title={t('dashboard.totalScans')}
              value={stats.totalScans}
              icon={<Target className="w-5 h-5" />}
              color="cyan"
              miniChart={<ScansMiniChart data={chartData.scans} />}
            />
          </div>

          {/* Completed Scans */}
          <StatCard
            title={t('dashboard.completedScans')}
            value={stats.completedScans}
            icon={<CheckCircle className="w-5 h-5" />}
            color="green"
            miniChart={<CompletedMiniChart data={chartData.completed} />}
          />

          {/* Vulnerabilities */}
          <div onClick={() => navigate('/vulnerabilities')} className="cursor-pointer">
            <StatCard
              title={t('vulnerabilities.title')}
              value={`${stats.totalVulnerabilities} (${stats.criticalVulnerabilities} ${t('dashboard.critical')})`}
              icon={<AlertTriangle className="w-5 h-5" />}
              color={stats.criticalVulnerabilities > 0 ? 'red' : 'yellow'}
              miniChart={<VulnMiniChart data={chartData.vulnerabilities} />}
            />
          </div>
        </div>

        {/* System Resources - Enhanced */}
        <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden">
          {/* Header with server info */}
          <div className="p-4 border-b border-gray-800 bg-gradient-to-r from-brand-green/5 to-transparent">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="p-2 bg-brand-green/20 rounded-lg flex-shrink-0">
                  <Activity className="w-6 h-6 text-brand-green" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-white break-words">{t('dashboard.serverPerformance')}</h2>
                  <p className="text-xs text-gray-500 break-words">{t('dashboard.infrastructureTotal')} - {systemResources.infrastructure?.server_count || 0} {t('dashboard.resources.servers')}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs flex-shrink-0">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-brand-green animate-pulse flex-shrink-0"></div>
                  <span className="text-gray-400 whitespace-nowrap">{t('dashboard.online')}</span>
                </div>
                <div className="px-3 py-1 bg-brand-black rounded-lg border border-gray-700 whitespace-nowrap">
                  <span className="text-gray-500">{t('dashboard.uptime')}: </span>
                  <span className="text-brand-green font-mono">99.9%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Resource Cards */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* CPU Card - Total & Available Infrastructure Cores */}
              <div className="bg-brand-black rounded-xl p-5 border border-cyan-500/20 hover:border-cyan-500/40 transition-all">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-cyan-500/10 rounded-xl">
                      <Cpu className="w-7 h-7 text-cyan-400" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">{t('dashboard.resources.cpuCores')}</div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-cyan-400">{systemResources.infrastructure?.total_cores || 0}</span>
                        <span className="text-sm text-gray-500">/ {systemResources.infrastructure?.total_cores || 0}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right max-w-[120px]">
                    <div className="text-xs px-2 py-1 rounded bg-cyan-500/20 text-cyan-400 truncate" title={t('dashboard.resources.availableTotal')}>
                      {t('dashboard.resources.availableTotal')}
                    </div>
                  </div>
                </div>
                {/* Visual representation - Available vs Total */}
                <div className="w-full bg-gray-800 rounded-full h-3 overflow-hidden mb-3">
                  <div
                    className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-cyan-500 to-cyan-400"
                    style={{ width: '100%' }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 gap-2">
                  <span className="truncate flex-1 min-w-0" title={`${systemResources.infrastructure?.server_count || 0} ${t('dashboard.resources.servers')}`}>{systemResources.infrastructure?.server_count || 0} {t('dashboard.resources.servers')}</span>
                  <span className="flex-shrink-0 truncate max-w-[100px]" title={t('dashboard.resources.computePower')}>{t('dashboard.resources.computePower')}</span>
                </div>
              </div>

              {/* RAM Card - Total & Available Infrastructure RAM */}
              <div className="bg-brand-black rounded-xl p-5 border border-purple-500/20 hover:border-purple-500/40 transition-all">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-purple-500/10 rounded-xl">
                      <MemoryStick className="w-7 h-7 text-purple-400" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">{t('dashboard.resources.ramInfra')}</div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-purple-400">{systemResources.infrastructure?.total_ram_gb || 0} GB</span>
                        <span className="text-sm text-gray-500">/ {systemResources.infrastructure?.total_ram_gb || 0} GB</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right max-w-[120px]">
                    <div className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-400 mb-1 truncate" title={`${systemResources.infrastructure?.server_count || 0} ${t('dashboard.resources.onlineCount')}`}>
                      {systemResources.infrastructure?.server_count || 0} {t('dashboard.resources.onlineCount')}
                    </div>
                  </div>
                </div>
                {/* Visual representation - Available vs Total */}
                <div className="w-full bg-gray-800 rounded-full h-3 overflow-hidden mb-3">
                  <div
                    className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-purple-500 to-purple-400"
                    style={{ width: '100%' }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 gap-2">
                  <span className="truncate flex-1 min-w-0" title={`${systemResources.infrastructure?.server_count || 0} ${t('dashboard.resources.servers')}`}>{systemResources.infrastructure?.server_count || 0} {t('dashboard.resources.servers')}</span>
                  <span className="flex-shrink-0 truncate max-w-[100px]" title={t('dashboard.resources.availableTotal')}>{t('dashboard.resources.availableTotal')}</span>
                </div>
              </div>

              {/* AI/LLM Card - Dynamic from Ollama */}
              <div className="bg-brand-black rounded-xl p-5 border border-green-500/20 hover:border-green-500/40 transition-all">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-green-500/10 rounded-xl">
                      <Zap className="w-7 h-7 text-green-400" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">IA / LLM</div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-green-400">
                          {(() => {
                            const models = llmInfo?.models_loaded || [];
                            const m = models.find(x => x.startsWith('sylink/')) || models[0];
                            if (!m) return 'SYLink 32B';
                            if (m.startsWith('sylink/sylink:')) return 'SYLink ' + m.split(':')[1].toUpperCase();
                            if (m.includes('sylink/sylink2-coder')) return 'Qwen2.5 ' + (m.split(':')[1] || '32B').toUpperCase();
                            return m.replace('sylink/sylink:', 'SYLink ').replace(/(\d+)b$/i, '$1B');
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right max-w-[120px]">
                    <div className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-400 mb-1 truncate">
                      {llmInfo?.compute_type || (llmInfo?.has_gpu ? 'GPU' : 'CPU')} Mode
                    </div>
                    <div className={`text-xs px-2 py-1 rounded truncate ${
                      llmInfo?.available ? 'bg-brand-green/20 text-brand-green' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {llmInfo?.available ? t('dashboard.resources.aiReady') : 'IA Indisponible'}
                    </div>
                  </div>
                </div>
                {/* Memory usage bar */}
                <div className="w-full bg-gray-800 rounded-full h-3 overflow-hidden mb-3">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      llmInfo?.available ? 'bg-gradient-to-r from-green-500 to-green-400' : 'bg-gray-600'
                    }`}
                    style={{ width: llmInfo?.has_gpu
                      ? `${llmInfo.gpu_memory_total > 0 ? Math.min((llmInfo.gpu_memory_used / llmInfo.gpu_memory_total) * 100, 100) : 0}%`
                      : `${llmInfo?.ram_total_gb > 0 ? Math.min((llmInfo.ram_used_gb / llmInfo.ram_total_gb) * 100, 100) : 100}%`
                    }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 gap-2">
                  <span className="truncate flex-1 min-w-0">
                    {llmInfo?.models_loaded?.length || 1} {t('dashboard.resources.model')} IA
                  </span>
                  <span className="flex-shrink-0 truncate max-w-[140px]">
                    {llmInfo?.has_gpu
                      ? `VRAM: ${llmInfo.gpu_memory_used?.toFixed(1) || '0'}/${llmInfo.gpu_memory_total?.toFixed(0) || '0'} GB`
                      : `RAM: ${llmInfo?.ram_used_gb?.toFixed(1) || '-'}/${llmInfo?.ram_total_gb?.toFixed(0) || '-'} GB`
                    }
                  </span>
                </div>
              </div>
            </div>

            {/* Additional Server Info */}
            <div className="mt-6 pt-4 border-t border-gray-800">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-brand-black rounded-lg border border-gray-800 overflow-hidden">
                  <div className="text-xs text-gray-500 mb-1 truncate" title={t('dashboard.activeProcesses')}>{t('dashboard.activeProcesses')}</div>
                  <div className="text-lg font-bold text-white truncate">{stats.activeScans || 0}</div>
                </div>
                <div className="text-center p-3 bg-brand-black rounded-lg border border-gray-800 overflow-hidden">
                  <div className="text-xs text-gray-500 mb-1 truncate" title={t('dashboard.scansToday')}>{t('dashboard.scansToday')}</div>
                  <div className="text-lg font-bold text-brand-green truncate">{stats.totalScans || 0}</div>
                </div>
                <div className="text-center p-3 bg-brand-black rounded-lg border border-gray-800 overflow-hidden">
                  <div className="text-xs text-gray-500 mb-1 truncate" title={t('dashboard.networkLoad')}>{t('dashboard.networkLoad')}</div>
                  <div className="text-lg font-bold text-cyan-400 truncate" title={t('dashboard.resources.normal')}>{t('dashboard.resources.normal')}</div>
                </div>
                <div className="text-center p-3 bg-brand-black rounded-lg border border-gray-800 overflow-hidden">
                  <div className="text-xs text-gray-500 mb-1 truncate" title={t('dashboard.temperature')}>{t('dashboard.temperature')}</div>
                  <div className="text-lg font-bold text-green-400 truncate" title={t('dashboard.resources.temperatureOk')}>{t('dashboard.resources.temperatureOk')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* LLM Schema - Below System Resources */}
        <LLMSchema
          attackTools={attackTools}
          targets={dynamicTargets}
          scanStatus={scanStatus}
          stats={stats}
          isActive={true}
          cpuUsage={systemResources.cpu.percent}
          gpuUsage={systemResources.gpu.available ? systemResources.gpu.percent : 0}
        />

        {/* Active Scans and Recent Activity - Side by Side with equal height */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Active Scans */}
          <div className="bg-brand-black-light rounded-xl p-6 border border-brand-green/30 flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <PlayCircle className="w-6 h-6 text-brand-green" />
                <h2 className="text-xl font-bold text-brand-green">{t('dashboard.activeScans')}</h2>
              </div>
              <span className="text-sm text-gray-500">{stats.activeScans} {t('scans.status.running')}</span>
            </div>
            <div className="space-y-3 flex-1">
              {activeScans.length > 0 ? (
                activeScans.map((scan) => (
                  <div
                    key={scan.id}
                    onClick={() => navigate(`/scans/${scan.id}`)}
                    className="bg-brand-black rounded-lg p-4 border border-gray-800 hover:border-brand-green/50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <span className="font-medium text-white truncate min-w-0 flex-1">{scan.target}</span>
                      <span className="badge bg-brand-green/20 text-brand-green flex-shrink-0">{scan.status}</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                      <div className="bg-brand-green h-full rounded-full animate-pulse-green" style={{ width: '50%' }}></div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-2 overflow-hidden">
                      <Clock className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{t('dashboard.started')}: {new Date(scan.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500 flex-1 flex flex-col items-center justify-center">
                  <PlayCircle className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                  <p>{t('dashboard.noActiveScans')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-brand-black-light rounded-xl p-6 border border-brand-green/30 flex flex-col">
            <div className="flex items-center gap-2 mb-6">
              <Activity className="w-6 h-6 text-brand-green" />
              <h2 className="text-xl font-bold text-brand-green">{t('dashboard.recentActivity')}</h2>
            </div>
            <div className="space-y-3 flex-1">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3 p-3 bg-brand-black rounded-lg border border-gray-800">
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${activity.type === 'scan_completed' ? 'bg-brand-green' : 'bg-blue-500'}`}></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate">{activity.title}</div>
                      <div className="text-xs text-gray-500 truncate">{activity.description}</div>
                      <div className="text-xs text-gray-600 mt-1 truncate">{new Date(activity.timestamp).toLocaleString()}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500 flex-1 flex flex-col items-center justify-center">
                  <Activity className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                  <p>{t('dashboard.noRecentActivity')}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions - Both buttons same width */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <PlanRestrictedButton
            feature="scan_basic"
            quotaType="scans_per_month"
            onClick={() => navigate('/scans/new')}
            data-tour="new-scan"
            className="btn-green text-left p-6 rounded-xl flex items-center gap-4 hover:scale-[1.02] transition-transform overflow-hidden"
            disabledClassName="!bg-gray-700 !text-gray-400"
            tooltipPosition="bottom"
          >
            <Target className="w-10 h-10 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-bold text-lg truncate">{t('dashboard.newScan')}</div>
              <div className="text-sm opacity-80 truncate">{t('dashboard.startAssessment')}</div>
            </div>
          </PlanRestrictedButton>
          <PlanRestrictedButton
            feature="report_executive"
            onClick={() => navigate('/reports')}
            className="bg-brand-black-lighter border border-brand-green/50 text-white p-6 rounded-xl flex items-center gap-4 hover:bg-brand-black-light hover:scale-[1.02] transition-all overflow-hidden"
            disabledClassName="!bg-gray-700 !border-gray-600 !text-gray-400"
            tooltipPosition="bottom"
          >
            <FileText className="w-10 h-10 text-brand-green flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-bold text-lg text-brand-green truncate">{t('reports.generateReport')}</div>
              <div className="text-sm text-gray-400 truncate">{t('dashboard.exportResults')}</div>
            </div>
          </PlanRestrictedButton>
        </div>
      </div>
    </div>
  );
};

const ResourceBar = ({ icon, label, value, details }) => {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm text-gray-400">{label}</span>
        </div>
        <span className="text-sm font-bold text-brand-green">{value}%</span>
      </div>
      <div className="w-full bg-gray-800 rounded-full h-3 overflow-hidden">
        <div
          className="bg-gradient-green h-full rounded-full transition-all duration-500"
          style={{ width: `${value}%` }}
        ></div>
      </div>
      {details && (
        <div className="text-xs text-gray-500 mt-1">{details}</div>
      )}
    </div>
  );
};

export default Dashboard;
